import React, { useContext, useEffect, useRef, useState } from 'react'
import { ActionPlanStatus, FinancialYear, FinancialYearStatus } from '../api/schemas/schema'
import { Layout } from './Layout'
import { t } from '../i18n/i18n'
import { Button, SelectTabData, Tab } from '@fluentui/react-components'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { DataContext } from '../context/DataContext'
import { Loader } from '../components/common/spinner/Loader'
import { FinancialYearTabs } from '../helpers/riskEconomyHelper'
import {
   Info20Regular,
   MoneyCalculator20Filled,
   DataTrending20Filled,
   DataTrending20Regular,
   Info20Filled,
   MoneyCalculator20Regular,
   TaskListSquareLtr20Filled,
   TaskListSquareLtr20Regular,
   LockClosed20Regular,
   LockClosed24Regular,
} from '@fluentui/react-icons'
import { CostIdentifying } from '../components/riskEconomy/CostIdentifying'
import { IItemState } from '../interfaces/IItemState'
import { FinancialYearNavLinks } from '../components/riskEconomy/FinancialYearNavLinks'
import { FinancialYearInformation } from '../components/riskEconomy/FinancialYearInformation'
import { CostEvaluation } from '../components/riskEconomy/CostEvaluation/CostEvaluation'
import { FinancialYearActionPlan } from '../components/riskEconomy/FinancialYearActionPlan'
import { fetchFinancialYear, isFinancialYearOwnerOrCreator } from '../helpers/financialYearHelper'
import { isSystemAdmin } from '../helpers/permissionHelper'
import { ModalCompleteFinancialYear } from '../components/riskEconomy/ModalCompleteFinancialYear'
import dayjs, { Dayjs } from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import { NavigationLink } from '../interfaces/INavigationLink'
dayjs.extend(isSameOrAfter)

export const SelectedFinancialYear = () => {
   const [searchParams, setSearchParams] = useSearchParams()
   const [financialYearState, setFinancialYearState] = useState<IItemState<FinancialYear>>({
      disabled: false,
      loading: true,
      error: undefined,
      item: null,
   })
   const [completeModalOpen, setCompleteModalOpen] = useState<boolean>(false)
   const { item: selectedFinancialYear, disabled } = financialYearState
   const navigate = useNavigate()
   const dataContext = useContext(DataContext)
   const { userAccount } = dataContext.state
   const { id } = useParams()
   const prevId = useRef({})
   const selectedTab = searchParams.get('tab')
   const [completeBtnDisabled, setCompleteBtnDisabled] = useState<boolean>(false)

   useEffect(() => {
      if (prevId.current !== id) {
         prevId.current = id
         fetchFinancialYear(dataContext, id, navigate, setFinancialYearState)
      }
      if (selectedFinancialYear && selectedFinancialYear.organisationId !== dataContext.state.organisation.id) {
         navigate(NavigationLink.RiskEconomy)
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [id, dataContext.state.userAccount])

   useEffect(() => {
      if (financialYearState?.item) {
         if (!isFinancialYearOwnerOrCreator(financialYearState.item, userAccount) && !isSystemAdmin(userAccount)) {
            setFinancialYearState((prev) => ({ ...prev, disabled: true }))
         }
      }
   }, [financialYearState, userAccount])

   const compareDate = (today: Dayjs, endDate: Dayjs) => {
      return endDate.isSameOrAfter(today, 'day')
   }

   useEffect(() => {
      let actionRequiredCosts = []
      if (selectedFinancialYear !== null) {
         actionRequiredCosts = selectedFinancialYear.costs.filter((c) => c.actionRequired)
         if (
            selectedFinancialYear.status === FinancialYearStatus.Completed ||
            selectedFinancialYear.actionPlans.length < actionRequiredCosts.length ||
            selectedFinancialYear.actionPlans.some((ap) => ap.status === ActionPlanStatus.NotDecided) ||
            compareDate(dayjs(), dayjs(selectedFinancialYear.endDate))
         ) {
            setCompleteBtnDisabled(true)
         } else {
            setCompleteBtnDisabled(false)
         }
      } else {
         setCompleteBtnDisabled(true)
      }
   }, [selectedFinancialYear])

   useEffect(() => {
      if (searchParams.get('tab') === null) {
         setSearchParams((prev) => ({ ...prev, tab: 'info' }))
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [searchParams])

   return (
      <Layout
         activeDesktopLink={NavigationLink.RiskEconomy}
         phoneNavLinks={
            <FinancialYearNavLinks
               id={selectedFinancialYear ? selectedFinancialYear.id : null}
               activeTab={selectedTab}
               actionTab={<Tab value={'complete'} icon={<LockClosed24Regular />} disabled={disabled || completeBtnDisabled} />}
            />
         }
         customOnTabSelect={(e, data: SelectTabData) => {
            if (data.value === 'complete') setCompleteModalOpen(true)
            else navigate(data.value)
         }}
         breadCrumbItems={[
            { path: NavigationLink.RiskEconomy, title: t('RiskEconomy'), active: false },
            { path: `${NavigationLink.RiskEconomy}/${selectedFinancialYear?.id}`, title: selectedFinancialYear?.name, active: true },
         ]}
         overflowAuto={selectedTab === FinancialYearTabs.Info ? true : false}
         tabs={
            <>
               <Tab value={FinancialYearTabs.Info} icon={selectedTab === FinancialYearTabs.Info ? <Info20Filled /> : <Info20Regular />}>
                  {t('Information')}
               </Tab>
               <Tab
                  value={FinancialYearTabs.IdentifyCosts}
                  icon={selectedTab === FinancialYearTabs.IdentifyCosts ? <MoneyCalculator20Filled /> : <MoneyCalculator20Regular />}
               >
                  {t('IdentifyCosts')}
               </Tab>
               <Tab
                  value={FinancialYearTabs.Evaluation}
                  icon={selectedTab === FinancialYearTabs.Evaluation ? <DataTrending20Filled /> : <DataTrending20Regular />}
               >
                  {t('Evaluation')}
               </Tab>
               <Tab
                  value={FinancialYearTabs.ActionPlan}
                  icon={selectedTab === FinancialYearTabs.ActionPlan ? <TaskListSquareLtr20Filled /> : <TaskListSquareLtr20Regular />}
               >
                  {t('ActionPlan')}
               </Tab>
            </>
         }
         button={
            <Button
               disabled={disabled || completeBtnDisabled}
               icon={<LockClosed20Regular />}
               onClick={() => setCompleteModalOpen(true)}
               appearance="transparent"
            >
               {t('CompleteFinancialYear')}
            </Button>
         }
      >
         {financialYearState.loading ? (
            <Loader />
         ) : (
            <>
               {selectedTab === FinancialYearTabs.Info && (
                  <FinancialYearInformation setFinancialYearState={setFinancialYearState} financialYearState={financialYearState} />
               )}
               {selectedTab === FinancialYearTabs.IdentifyCosts && (
                  <CostIdentifying setFinancialYearState={setFinancialYearState} financialYearState={financialYearState} />
               )}
               {selectedTab === FinancialYearTabs.Evaluation && (
                  <CostEvaluation setFinancialYearState={setFinancialYearState} financialYearState={financialYearState} />
               )}
               {selectedTab === FinancialYearTabs.ActionPlan && (
                  <FinancialYearActionPlan setFinancialYearState={setFinancialYearState} financialYearState={financialYearState} />
               )}
            </>
         )}
         <ModalCompleteFinancialYear
            open={completeModalOpen}
            close={() => setCompleteModalOpen(false)}
            financialYearState={financialYearState}
            setFinancialYearState={setFinancialYearState}
         />
      </Layout>
   )
}
